import toModuleVoucher from 'Redux/reselects/order-calc/to-module-voucher';
import normalisePickAndMix, { isActivePickAndMix } from 'Redux/reselects/order-calc/normalise-pick-and-mixes';
import tierPricing from 'Redux/reselects/order-calc/tier-pricing';

/**
 * The facet keys the module reads for voucher eligibility (D10, spec §4).
 * The same list as amen build-pricing-context.js VOUCHER_FACET_KEYS.
 */
export const VOUCHER_FACET_KEYS = ['collections', 'franchises', 'publishers'];

// Facet values can be objects with a name, or plain strings (D10, cartcalc.js:171)
const toFacets = item => VOUCHER_FACET_KEYS.reduce((facets, key) => {
  if (Array.isArray(item[key])) {
    facets[key] = item[key].map(value => String(value?.name ?? value));
  }
  return facets;
}, {});

/**
 * One cart item (amen redis-basket _formatProduct) → one context item.
 * For a bundle, the basket `price` is already the price of the selected tier,
 * the same value as amen dbProduct.bundles[tierIndex].price. For PWYW, the
 * basket `price` is the PWYW value.
 */
const toContextItem = (item, currencyCode) => {
  const price = item.price?.[currencyCode];

  if (!price && price !== 0) {
    throw new Error(`Price missing in ${currencyCode} - ${item._id}`);
  }

  const payWhatYouWant = item.pay_what_you_want
    ? { value: Math.round(item.pay_what_you_want.value) }
    : null;
  const mystery = !!item.mystery;
  const listPrice = payWhatYouWant ? payWhatYouWant.value : Math.round(price);
  // The module has no "was" price: a non-SRP bundle is charged its listPrice
  // (or the pledge, for pay what you want), and the cart shows what its games
  // are worth (legacy cartcalc.js displayPrice; order-calc 1.0.7, catalogue
  // D30; Stage 7 decision D9). The worth is the basket fullPrice, or the sum
  // of the prices of the games in its tiers (a PWYW item has no fullPrice). A
  // mystery bundle and an SRP bundle have a single price. The reference price
  // is display only, and sent only when it differs from listPrice (the module
  // raises it to listPrice or to what the line costs, so a saving is never
  // negative).
  const hasWorth = item.type === 'bundle' && !item.is_srp_bundle && !mystery;
  const worth = hasWorth
    ? Math.round(item.fullPrice?.[currencyCode] ?? tierPricing(item.tiers, currencyCode))
    : 0;

  return {
    id: String(item._id),
    quantity: item.quantity,
    type: item.type,
    listPrice,
    isStarDeal: !!item.current_discount?.star_deal,
    // Mystery bundles are single-priced (spec §6 amendment 2026-08-17)
    promoDiscount: mystery ? 0 : (item.current_discount?.percent ?? 0),
    isSrpBundle: !!item.is_srp_bundle,
    payWhatYouWant,
    // Display only. The module never prices from it
    ...(worth > 0 && worth !== listPrice ? { referencePrice: worth } : {}),
    mystery,
    giveaway: !!item.giveaway,
    tierIndex: item.tierIndex ?? null,
    supplierId: String(item.supplier_id ?? ''),
    // The basket field is releaseDate (redis-basket.js, from the product's
    // release_date). Basket items have no release_date: the legacy
    // cartcalc.js check of `release_date` is dead code (catalogue D7).
    releaseDate: item.releaseDate ? new Date(item.releaseDate).toISOString() : null,
    facets: toFacets(item),
  };
};

/**
 * Build a PricingContext (holocron order-calc spec §4) from Redux state.
 * This is the frontend column of the spec §4 builder table. It mirrors amen
 * app/services/order-calc/context-builder/build-pricing-context.js.
 *
 * Pure and synchronous. The caller supplies `now`, the country and the
 * currency, so this function does not read window or the clock.
 *
 * location.administrativeArea is always null: the core result does not use
 * it, because all tax is privileged (spec §8).
 *
 * @param   {Object}   input
 * @param   {Object}   input.cart          state.cart
 * @param   {Object[]} input.pickAndMixAll state.pickAndMix.all
 * @param   {Object}   input.voucher       state.voucher.voucher ({} when no voucher)
 * @param   {string}   input.currencyCode  ISO 4217
 * @param   {string}   input.countryCode   ISO 3166-1 alpha-2
 * @param   {number}   input.now           Epoch milliseconds
 * @returns {Object}                       PricingContext
 * @throws  {Error}                        On duplicate item ids, a missing price
 *                                         for the currency, or an invalid voucher
 *                                         (see toModuleVoucher).
 */
export default function buildPricingContext({
  cart,
  pickAndMixAll,
  voucher,
  currencyCode,
  countryCode,
  now,
}) {
  const cartItems = cart?.items ?? [];
  const itemIds = cartItems.map(item => String(item._id));
  const duplicateIds = itemIds.filter((id, i) => itemIds.indexOf(id) !== i);

  if (duplicateIds.length > 0) {
    throw new Error(`Duplicate basket item ids: ${duplicateIds.join(', ')}`);
  }

  const context = {
    now,
    currencyCode,
    location: {
      countryCode,
      administrativeArea: null,
    },
    items: cartItems.map(item => toContextItem(item, currencyCode)),
    pickAndMixes: (pickAndMixAll ?? [])
      .filter(pickAndMix => isActivePickAndMix(pickAndMix, now))
      .filter(pickAndMix => (pickAndMix.products ?? [])
        .some(product => itemIds.includes(String(product?._id ?? product))))
      .map(pickAndMix => normalisePickAndMix(pickAndMix, currencyCode)),
  };

  if (voucher?.code) {
    context.voucher = toModuleVoucher(voucher, currencyCode);
  }

  return context;
}
